import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, CartesianGrid, Cell, Legend
} from 'recharts';
import { Activity, ArrowLeft, Printer } from 'lucide-react';
import apiService from '../../services/apiService';
import { formatMillions, formatPct } from './forecastUsers';
import { ftpRateFor, splitNiiFtp } from './forecastAdjustments';
import ForecastPeerProForma from './ForecastPeerProForma';
import ForecastGrowthMatrix from './ForecastGrowthMatrix';
import ForecastGuardrailBoard, {
  statusFor, toBulletPercent, higherIsBetterBands, lowerIsBetterBands
} from './ForecastGuardrailBoard';

// Read-only executive review. Ungated by design decision: filters let a
// divisional leader scope to their own units or read the whole bank.

const GOLD = '#FFAC03';
const MUTED = 'var(--ace-chart-2, #6b7c93)';
const GREEN = '#2f9e6e';
const RED = '#d9534f';

const CYCLE_CODE = 'FY2027_BUDGET';
const BRIDGE_YEARS = ['2026', '2027', '2028', '2029', '2030'];
const FALLBACK_EXEC_MACRO = {
  macroCase: 'Base',
  sofr: 4.85,
  ftpRatePct: ftpRateFor(4.85),
  depositRunoff: 3.8,
  creditLoss: 0.42,
  rwaLoanGrowthCoefficient: 0.82,
  loanGrowthAppetiteCap: 8.5
};

const annualizeYtd = (value, months) => (months ? (value / months) * 12 : value);
const pickNumber = (...values) => {
  for (const value of values) {
    if (value === null || value === undefined || value === '') continue;
    const number = Number(value);
    if (Number.isFinite(number)) return number;
  }
  return null;
};
const pickSettingNumber = (values, fallback) => {
  for (const value of values) {
    if (value === null || value === undefined || value === '') continue;
    const number = Number(value);
    if (Number.isFinite(number)) return { value: number, fromSettings: true };
  }
  return { value: fallback, fromSettings: false };
};

const normalizeMacroCaseLabel = (value) => {
  const raw = String(value || FALLBACK_EXEC_MACRO.macroCase).trim();
  const stripped = raw.replace(/^central[_\s-]*/i, '');
  const label = stripped
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(' ');
  return label || FALLBACK_EXEC_MACRO.macroCase;
};

const formatSettingsTimestamp = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  });
};

const deriveExecMacroSettings = (centralSettings, source) => {
  const settings = centralSettings?.settings || {};
  const macroBase = centralSettings?.macroBase || {};
  const ftp = settings.ftp || centralSettings?.ftp || {};
  const drivers = settings.drivers || centralSettings?.drivers || {};
  const guardrails = settings.guardrails || centralSettings?.guardrails || {};
  const loanGrowthGuardrail = guardrails.loanGrowth || guardrails.loanGrowthAppetite || {};
  const sofr = pickSettingNumber([ftp.sofrPct, macroBase.sofr], FALLBACK_EXEC_MACRO.sofr);
  const transferSpread = pickSettingNumber(
    [ftp.transferSpreadPct],
    FALLBACK_EXEC_MACRO.ftpRatePct - FALLBACK_EXEC_MACRO.sofr
  );
  const directFtpRate = pickSettingNumber([ftp.ftpRatePct, centralSettings?.ftpRatePct], null);
  const depositRunoff = pickSettingNumber([
    drivers.depositRunoffPct,
    drivers.depositRunoff,
    macroBase.depositRunoff,
    centralSettings?.depositRunoff
  ], FALLBACK_EXEC_MACRO.depositRunoff);
  const creditLoss = pickSettingNumber([
    drivers.creditLossPct,
    drivers.creditLoss,
    macroBase.creditLoss,
    centralSettings?.creditLoss
  ], FALLBACK_EXEC_MACRO.creditLoss);
  const requiredSettingFields = [
    directFtpRate.fromSettings || sofr.fromSettings,
    depositRunoff.fromSettings,
    creditLoss.fromSettings
  ];
  const populatedRequiredFields = requiredSettingFields.filter(Boolean).length;
  const resolvedSource = source !== 'central-settings'
    ? 'fallback-default'
    : populatedRequiredFields === requiredSettingFields.length
      ? 'central-settings'
      : populatedRequiredFields > 0
        ? 'partial-settings'
        : 'fallback-default';

  return {
    source: resolvedSource,
    macroCase: normalizeMacroCaseLabel(settings.macroCase || centralSettings?.macroCase || macroBase.label),
    updatedAt: centralSettings?.updatedAt || centralSettings?.approvedAt || null,
    ftpRatePct: directFtpRate.value ?? (sofr.value + transferSpread.value),
    depositRunoff: depositRunoff.value,
    creditLoss: creditLoss.value,
    rwaLoanGrowthCoefficient: pickNumber(
      drivers.rwaLoanGrowthCoefficient,
      drivers.rwaGrowthCoefficient,
      FALLBACK_EXEC_MACRO.rwaLoanGrowthCoefficient
    ),
    loanGrowthAppetiteCap: pickNumber(
      loanGrowthGuardrail.appetiteCap,
      loanGrowthGuardrail.yellow,
      guardrails.loanGrowthAppetiteCap,
      centralSettings?.centralItems?.loanGrowthLimiterPct,
      FALLBACK_EXEC_MACRO.loanGrowthAppetiteCap
    )
  };
};

const emptyCycleScope = () => ({
  total: 0,
  submittedOrBeyond: 0,
  awaitingReview: 0,
  approvedOrLoaded: 0,
  returned: 0,
  inProgress: 0,
  notStarted: 0
});

const addStatusToScope = (scope, status) => {
  scope.total += 1;
  if (status === 'submitted') {
    scope.awaitingReview += 1;
    scope.submittedOrBeyond += 1;
  } else if (status === 'approved' || status === 'loaded') {
    scope.approvedOrLoaded += 1;
    scope.submittedOrBeyond += 1;
  } else if (status === 'returned') {
    scope.returned += 1;
  } else if (status === 'in_progress') {
    scope.inProgress += 1;
  } else {
    scope.notStarted += 1;
  }
  return scope;
};

const scopeFromSubmissions = (submissions) =>
  (submissions || []).reduce((scope, submission) => addStatusToScope(scope, submission.status), emptyCycleScope());

const scopeForExecFilters = (scope, divisionFilter, marketFilter) => {
  if (!scope) return null;
  if (divisionFilter === 'all' && marketFilter === 'all') return scope;
  const coverage = scope.coverage;
  if (!coverage) return null;
  if (divisionFilter !== 'all' && marketFilter !== 'all') {
    return coverage.divisionMarkets?.[`${divisionFilter}|${marketFilter}`] || emptyCycleScope();
  }
  if (divisionFilter !== 'all') {
    return coverage.divisions?.[divisionFilter] || emptyCycleScope();
  }
  return coverage.markets?.[marketFilter] || emptyCycleScope();
};

const floatRows = (steps) => {
  let cumulative = 0;
  return steps.map((step) => {
    if (step.total) {
      cumulative = step.value;
      return { label: step.label, base: 0, delta: step.value, tone: 'total', signed: step.value };
    }
    const base = step.value >= 0 ? cumulative : cumulative + step.value;
    cumulative += step.value;
    return { label: step.label, base, delta: Math.abs(step.value), tone: step.value >= 0 ? 'pos' : 'neg', signed: step.value };
  });
};

// Ranged-bar waterfall: the numeric axis zooms to the data envelope plus a
// buffer, so by-group attribution floats are NOT compressed against zero.
const WaterfallBar = ({ data, height = 270, unit = '$MM' }) => {
  if (!data.length) return <div style={{ height }} />;
  const tops = data.map((row) => (row.tone === 'total' ? row.delta : row.base + row.delta));
  const bottoms = data.map((row) => (row.tone === 'total' ? row.delta : row.base));
  const hi = Math.max(...tops, ...bottoms);
  const lo = Math.min(...tops, ...bottoms);
  const span = Math.max(hi - lo, Math.abs(hi) * 0.05, 1);
  const domainMin = lo - span * 0.12;
  const domainMax = hi + span * 0.08;
  const rows = data.map((row) => ({
    ...row,
    range: row.tone === 'total' ? [domainMin, row.delta] : [row.base, row.base + row.delta]
  }));
  return (
    <div style={{ width: '100%', height }}>
      <ResponsiveContainer>
        <ComposedChart data={rows}>
          <CartesianGrid strokeDasharray="2 4" strokeOpacity={0.25} />
          <XAxis
            dataKey="label" interval={0}
            angle={rows.length > 5 ? -28 : 0}
            textAnchor={rows.length > 5 ? 'end' : 'middle'}
            height={rows.length > 5 ? 78 : 30}
            tick={{ fontSize: 10.5 }}
            tickFormatter={(label) => (String(label).length > 16 ? `${String(label).slice(0, 15)}…` : label)}
          />
          <YAxis domain={[domainMin, domainMax]} tickFormatter={(v) => `$${Math.round(v)}`} width={64} allowDataOverflow />
          <Tooltip
            formatter={(v, n, props) => [
              `${props?.payload?.signed >= 0 ? '+' : ''}$${Number(props?.payload?.signed).toFixed(1)}${unit === '$MM' ? 'MM' : ''}`,
              props?.payload?.label
            ]}
          />
          <Bar dataKey="range" radius={[3, 3, 0, 0]} isAnimationActive={false}>
            {rows.map((row) => (
              <Cell key={row.label} fill={row.tone === 'total' ? MUTED : row.tone === 'pos' ? GREEN : RED} />
            ))}
          </Bar>
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
};

const ForecastExecView = () => {
  const [groupBy, setGroupBy] = useState('division');
  const [divisionFilter, setDivisionFilter] = useState('all');
  const [marketFilter, setMarketFilter] = useState('all');
  const [glFilter, setGlFilter] = useState('all');
  const [bridgeYear, setBridgeYear] = useState('2026');
  const [bridgeMode, setBridgeMode] = useState('driver');
  // Executive review defaults to the GAAP basis; FTP is the internal lens.
  const [basis, setBasis] = useState('GAAP');

  const [entityAnnual, setEntityAnnual] = useState(null);
  const [groupAnnual, setGroupAnnual] = useState(null);
  const [companyMonthly, setCompanyMonthly] = useState(null);
  const [glMonthly, setGlMonthly] = useState(null);
  const [glGroupAnnual, setGlGroupAnnual] = useState(null);
  const [submissionsData, setSubmissionsData] = useState(null);
  const [centralSettings, setCentralSettings] = useState(null);
  const [centralSettingsSource, setCentralSettingsSource] = useState('fallback-default');
  const [error, setError] = useState(null);

  // EX_03B — AI executive narrative (materiality-anchored, drafted on demand).
  const [narrative, setNarrative] = useState(null);
  const [narrativeBusy, setNarrativeBusy] = useState(false);
  const [materialityPct, setMaterialityPct] = useState(10);

  const draftNarrative = async () => {
    setNarrativeBusy(true);
    try {
      const result = await apiService.fetchExecutiveNarrative({
        cycleCode: CYCLE_CODE, materialityPct
      });
      setNarrative(result);
    } catch (err) {
      setNarrative({ status: 'error', detail: err?.response?.data?.error || err.message });
    } finally {
      setNarrativeBusy(false);
    }
  };

  const filters = useMemo(() => ({
    division: divisionFilter === 'all' ? undefined : divisionFilter,
    market: marketFilter === 'all' ? undefined : marketFilter
  }), [divisionFilter, marketFilter]);

  useEffect(() => {
    apiService.fetchForecastSubmissions(CYCLE_CODE).then(setSubmissionsData).catch(() => {});
    apiService.fetchForecastComparison({ cycleCode: CYCLE_CODE, level: 'entity', grain: 'annual' })
      .then(setEntityAnnual).catch((err) => setError(err?.response?.data?.error || err.message));
  }, []);

  useEffect(() => {
    let active = true;
    apiService.fetchCentralSettings(CYCLE_CODE)
      .then((settings) => {
        if (!active) return;
        setCentralSettings(settings);
        setCentralSettingsSource('central-settings');
      })
      .catch(() => {
        if (!active) return;
        setCentralSettings(null);
        setCentralSettingsSource('fallback-default');
      });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    Promise.all([
      apiService.fetchForecastComparison({ cycleCode: CYCLE_CODE, level: groupBy, grain: 'annual', ...filters }),
      apiService.fetchForecastComparison({ cycleCode: CYCLE_CODE, level: 'company', grain: 'monthly', ...filters }),
      apiService.fetchForecastComparison({ cycleCode: CYCLE_CODE, level: 'company', grain: 'monthly', detail: 'glGroup', ...filters }),
      apiService.fetchForecastComparison({ cycleCode: CYCLE_CODE, level: groupBy, grain: 'annual', detail: 'glGroup', ...filters })
    ])
      .then(([group, monthly, gl, glGroup]) => {
        setGroupAnnual(group);
        setCompanyMonthly(monthly);
        setGlMonthly(gl);
        setGlGroupAnnual(glGroup);
      })
      .catch((err) => setError(err?.response?.data?.error || err.message));
  }, [groupBy, filters]);

  const execMacroSettings = useMemo(
    () => deriveExecMacroSettings(centralSettings, centralSettingsSource),
    [centralSettings, centralSettingsSource]
  );
  const execMacroChipLabel = useMemo(() => {
    const sourceLabel = execMacroSettings.source === 'central-settings'
      ? 'central settings'
      : execMacroSettings.source === 'partial-settings'
        ? 'partial central settings'
      : 'fallback defaults';
    const updatedAt = formatSettingsTimestamp(execMacroSettings.updatedAt);
    return `Macro case: ${execMacroSettings.macroCase} · ${sourceLabel}${updatedAt ? ` · updated ${updatedAt}` : ''}`;
  }, [execMacroSettings]);

  const divisions = useMemo(
    () => [...new Set((entityAnnual?.groups || []).map((g) => g.division))].filter(Boolean).sort(),
    [entityAnnual]
  );
  const markets = useMemo(
    () => [...new Set((entityAnnual?.groups || [])
      .filter((g) => divisionFilter === 'all' || g.division === divisionFilter)
      .map((g) => g.market))].filter(Boolean).sort(),
    [entityAnnual, divisionFilter]
  );
  const glGroups = useMemo(
    () => [...new Set((glMonthly?.groups || []).map((g) => g.glGroup))].sort(),
    [glMonthly]
  );

  // ---- headline (company scope under current filters) ----
  const companyAnnualRows = useMemo(() => {
    const points = {};
    (companyMonthly?.groups?.[0] ? Object.entries(companyMonthly.groups[0].series) : []).forEach(([name, rows]) => {
      const byYear = {};
      rows.forEach((row) => {
        const year = row.period.slice(0, 4);
        const bucket = byYear[year] = byYear[year] || { months: 0, netIncome: 0, nii: 0, fees: 0, expense: 0, provision: 0, tax: 0, interestIncome: 0, interestExpense: 0, loans: 0, deposits: 0, lastPeriod: '' };
        bucket.months += 1;
        ['netIncome', 'nii', 'fees', 'expense', 'provision', 'tax', 'interestIncome', 'interestExpense'].forEach((f) => { bucket[f] += row[f]; });
        if (row.period > bucket.lastPeriod) {
          bucket.lastPeriod = row.period;
          bucket.loans = row.loans;
          bucket.deposits = row.deposits;
        }
      });
      points[name] = byYear;
    });
    return points;
  }, [companyMonthly]);

  const execGrowthColumns = useMemo(() => {
    const actuals = companyAnnualRows.actuals || {};
    const prior = companyAnnualRows.prior || {};
    const toValues = (row, annualize) => {
      const months = row.months || 12;
      const factor = annualize ? 12 / months : 1;
      return {
        loans: row.loans / 1e6,
        deposits: row.deposits / 1e6,
        nii: (row.nii * factor) / 1e6,
        fees: (row.fees * factor) / 1e6,
        expense: (row.expense * factor) / 1e6,
        provision: (row.provision * factor) / 1e6,
        netIncome: (row.netIncome * factor) / 1e6
      };
    };
    const columns = [];
    if (actuals['2025']) columns.push({ label: 'FY2025A', sub: 'full year', actual: true, values: toValues(actuals['2025'], false) });
    if (actuals['2026']) {
      columns.push({
        label: '2026 YTD', sub: `${actuals['2026'].months || 5} mo · annualized`, actual: true,
        values: toValues(actuals['2026'], true)
      });
    }
    ['2027', '2028', '2029', '2030'].forEach((year) => {
      if (prior[year]) columns.push({ label: `FY${year}`, sub: 'prior plan', values: toValues(prior[year], false) });
    });
    return columns;
  }, [companyAnnualRows]);

  const actual2026 = companyAnnualRows.actuals?.['2026'];
  const prior2026 = companyAnnualRows.prior?.['2026'];
  const budget2026 = companyAnnualRows.budget?.['2026'];
  const runRateNi = actual2026 ? annualizeYtd(actual2026.netIncome, actual2026.months) : null;
  const cycleCompletenessScope = useMemo(() => {
    if (!submissionsData) return null;
    const serverScope = scopeForExecFilters(submissionsData.scope, divisionFilter, marketFilter);
    if (serverScope) return serverScope;
    const filteredSubmissions = (submissionsData.submissions || []).filter((submission) =>
      (divisionFilter === 'all' || submission.division === divisionFilter) &&
      (marketFilter === 'all' || submission.market === marketFilter));
    return scopeFromSubmissions(filteredSubmissions);
  }, [submissionsData, divisionFilter, marketFilter]);

  const headline = [
    { label: 'NI run-rate (2026 ann.)', value: runRateNi !== null ? formatMillions(runRateNi / 1e6) : '—' },
    {
      label: 'vs prior cycle plan',
      value: runRateNi !== null && prior2026 ? formatPct(((runRateNi - prior2026.netIncome) / Math.abs(prior2026.netIncome)) * 100) : '—',
      tone: runRateNi !== null && prior2026 ? (runRateNi >= prior2026.netIncome ? 'pos' : 'neg') : null
    },
    {
      label: 'vs budget',
      value: runRateNi !== null && budget2026 ? formatPct(((runRateNi - budget2026.netIncome) / Math.abs(budget2026.netIncome)) * 100) : '—',
      tone: runRateNi !== null && budget2026 ? (runRateNi >= budget2026.netIncome ? 'pos' : 'neg') : null
    },
    { label: 'Loan / deposit', value: actual2026 && actual2026.deposits ? `${((actual2026.loans / actual2026.deposits) * 100).toFixed(1)}%` : '—' },
    {
      label: 'Cycle completeness',
      value: cycleCompletenessScope
        ? `${cycleCompletenessScope.approvedOrLoaded || 0} of ${cycleCompletenessScope.total || 0} approved/loaded`
        : '—'
    }
  ];

  // ---- plan bridge for the selected year ----
  const bridge = useMemo(() => {
    const year = bridgeYear;
    const prevYear = String(Number(year) - 1);
    if (bridgeMode === 'driver' && glFilter === 'all') {
      const prior = companyAnnualRows.prior?.[year];
      if (!prior) return [];
      if (year === '2026' && actual2026) {
        const m = actual2026.months;
        return floatRows([
          { label: 'Prior plan NI', total: true, value: prior.netIncome / 1e6 },
          { label: 'NII', value: (annualizeYtd(actual2026.nii, m) - prior.nii) / 1e6 },
          { label: 'Fees', value: (annualizeYtd(actual2026.fees, m) - prior.fees) / 1e6 },
          { label: 'Expense', value: -(annualizeYtd(actual2026.expense, m) - prior.expense) / 1e6 },
          { label: 'Credit', value: -(annualizeYtd(actual2026.provision, m) - prior.provision) / 1e6 },
          { label: 'Tax', value: -(annualizeYtd(actual2026.tax, m) - prior.tax) / 1e6 },
          { label: 'Run-rate NI', total: true, value: annualizeYtd(actual2026.netIncome, m) / 1e6 }
        ]);
      }
      const previous = companyAnnualRows.prior?.[prevYear];
      if (!previous) return [];
      return floatRows([
        { label: `FY${prevYear} plan`, total: true, value: previous.netIncome / 1e6 },
        { label: 'NII', value: (prior.nii - previous.nii) / 1e6 },
        { label: 'Fees', value: (prior.fees - previous.fees) / 1e6 },
        { label: 'Expense', value: -(prior.expense - previous.expense) / 1e6 },
        { label: 'Credit', value: -(prior.provision - previous.provision) / 1e6 },
        { label: 'Tax', value: -(prior.tax - previous.tax) / 1e6 },
        { label: `FY${year} plan`, total: true, value: prior.netIncome / 1e6 }
      ]);
    }

    // group-contribution mode (and any GL-filtered bridge)
    const sourceGroups = glFilter === 'all' ? (groupAnnual?.groups || []) : (glGroupAnnual?.groups || []).filter((g) => g.glGroup === glFilter);
    const readValue = (series, year_) => {
      const row = (series || []).find((r) => r.period === year_);
      if (!row) return null;
      if (glFilter === 'all') return { value: row.netIncome, months: row.months };
      return { value: row.value, months: row.months };
    };
    const contributions = [];
    let startTotal = 0;
    let endTotal = 0;
    sourceGroups.forEach((group) => {
      const prior = readValue(group.series.prior, year);
      if (!prior) return;
      let endValue;
      if (year === '2026') {
        const actual = readValue(group.series.actuals, year);
        if (!actual) return;
        endValue = annualizeYtd(actual.value, actual.months);
      } else {
        const prev = readValue(group.series.prior, prevYear);
        if (!prev) return;
        startTotal += prev.value;
        endTotal += prior.value;
        contributions.push({ label: group.key, value: (prior.value - prev.value) / 1e6 });
        return;
      }
      startTotal += prior.value;
      endTotal += endValue;
      contributions.push({ label: group.key, value: (endValue - prior.value) / 1e6 });
    });
    contributions.sort((a, b) => Math.abs(b.value) - Math.abs(a.value));
    const top = contributions.slice(0, 8);
    const otherSum = contributions.slice(8).reduce((s, c) => s + c.value, 0);
    const steps = [
      { label: year === '2026' ? 'Prior plan' : `FY${String(Number(year) - 1)}`, total: true, value: startTotal / 1e6 },
      ...top,
      ...(contributions.length > 8 ? [{ label: 'All other', value: otherSum }] : []),
      { label: year === '2026' ? 'Run-rate' : `FY${year}`, total: true, value: endTotal / 1e6 }
    ];
    return floatRows(steps);
  }, [bridgeMode, bridgeYear, glFilter, companyAnnualRows, actual2026, groupAnnual, glGroupAnnual]);

  // ---- balance & bucket visuals ----
  // The forecast actually being worked on: each package's saved balance/fee
  // path, summed under the current filters; units with no saved path yet are
  // filled with their prior-plan values so the aggregate stays comparable.
  const workingPaths = useMemo(() => {
    const scoped = (submissionsData?.submissions || []).filter((s) =>
      (divisionFilter === 'all' || s.division === divisionFilter) &&
      (marketFilter === 'all' || s.market === marketFilter));
    const withPath = scoped.filter((s) =>
      Array.isArray(s.modelSnapshot?.balanceSheetPath) && s.modelSnapshot.balanceSheetPath.length);
    if (withPath.length === 0) return null;
    const priorByEntityYear = {};
    (entityAnnual?.groups || []).forEach((g) => {
      (g.series.prior || []).forEach((row) => {
        priorByEntityYear[`${g.key}|${row.period}`] = row;
      });
    });
    // 2031 only when every scoped unit has a saved path — the prior plan used
    // to fill the gaps ends at 2030, so a partial 2031 total would nosedive.
    const years = withPath.length === scoped.length
      ? [2027, 2028, 2029, 2030, 2031]
      : [2027, 2028, 2029, 2030];
    const totals = years.map((year) => ({ year, loans: 0, deposits: 0, fees: 0 }));
    scoped.forEach((s) => {
      const path = s.modelSnapshot?.balanceSheetPath;
      years.forEach((year, index) => {
        const fromPath = Array.isArray(path) ? path.find((p) => p.year === year) : null;
        if (fromPath) {
          totals[index].loans += (fromPath.loans || 0) * 1e6;
          totals[index].deposits += (fromPath.deposits || 0) * 1e6;
          totals[index].fees += (fromPath.fees || 0) * 1e6;
        } else {
          const prior = priorByEntityYear[`${s.planningEntity}|${year}`];
          if (prior) {
            totals[index].loans += prior.loans || 0;
            totals[index].deposits += prior.deposits || 0;
            totals[index].fees += prior.fees || 0;
          }
        }
      });
    });
    return { totals, coverage: withPath.length, units: scoped.length };
  }, [submissionsData, entityAnnual, divisionFilter, marketFilter]);

  // C3: server-computed working-plan coverage per GL family (saved forecast path
  // vs prior-plan fill). Rendered as badge chips on the EX_02A/EX_02B charts so
  // exec coverage counts come from the one server scope object, not client rows.
  const workingPlanCoverage = submissionsData?.scope?.workingPlanCoverage || null;
  const coverageBadge = (family) => {
    const line = workingPlanCoverage?.[family];
    if (!line) return null;
    const saved = line.savedForecastPath ?? line.savedPath ?? 0;
    const prior = line.priorPlanFill ?? line.priorFill ?? 0;
    const total = line.total ?? (saved + prior);
    return (
      <span className="forecast-exec-coverage-badges" style={{ display: 'inline-flex', gap: 6, marginLeft: 8 }}>
        <span className="forecast-health-chip green" title="Packages with a saved forecast path">
          {saved}/{total} saved path
        </span>
        {prior > 0 && (
          <span className="forecast-health-chip neutral" title="Packages filled from the prior plan">
            {prior} prior fill
          </span>
        )}
      </span>
    );
  };

  const balanceLines = useMemo(() => {
    const group = companyMonthly?.groups?.[0];
    if (!group) return [];
    const byPeriod = {};
    ['actuals', 'prior'].forEach((name) => {
      (group.series[name] || []).forEach((row) => {
        const point = byPeriod[row.period] = byPeriod[row.period] || { x: row.period };
        point[`loans_${name}`] = row.loans / 1e9;
        point[`deposits_${name}`] = row.deposits / 1e9;
      });
    });
    // Working plan lands as year-end points so it reads against the monthly axis.
    (workingPaths?.totals || []).forEach((total) => {
      if (!total.loans && !total.deposits) return;
      const x = `${total.year}-12`;
      const point = byPeriod[x] = byPeriod[x] || { x };
      point.loans_working = total.loans / 1e9;
      point.deposits_working = total.deposits / 1e9;
    });
    return Object.values(byPeriod).sort((a, b) => a.x.localeCompare(b.x));
  }, [companyMonthly, workingPaths]);

  const bucketMultiples = useMemo(() => {
    const groups = (glMonthly?.groups || []).filter((g) => ['Non-Interest Income', 'Non-Interest Expense'].includes(g.rollup));
    const withRecent = groups.map((group) => ({
      ...group,
      recent: (group.series.actuals || []).slice(-6).reduce((s, p) => s + Math.abs(p.value), 0)
    }));
    const pick = (rollup) => withRecent.filter((g) => g.rollup === rollup).sort((a, b) => b.recent - a.recent).slice(0, 4);
    const build = (group) => {
      const byPeriod = {};
      ['actuals', 'prior'].forEach((name) => {
        (group.series[name] || []).forEach((p) => {
          const row = byPeriod[p.period] = byPeriod[p.period] || { x: p.period };
          row[name] = p.value / 1e6;
        });
      });
      // Working overlay (NIR only): scale this bucket's prior-plan year by the
      // working-vs-prior total fee ratio — a labeled pro-rata view, year-end dots.
      if (group.rollup === 'Non-Interest Income' && workingPaths?.totals) {
        const priorFeesByYear = {};
        (glMonthly?.groups || [])
          .filter((g) => g.rollup === 'Non-Interest Income')
          .forEach((g) => (g.series.prior || []).forEach((p) => {
            const year = p.period.slice(0, 4);
            priorFeesByYear[year] = (priorFeesByYear[year] || 0) + p.value;
          }));
        workingPaths.totals.forEach((total) => {
          const priorTotal = priorFeesByYear[String(total.year)];
          if (!priorTotal || !total.fees) return;
          const bucketPriorYear = (group.series.prior || [])
            .filter((p) => p.period.startsWith(String(total.year)))
            .reduce((s2, p) => s2 + p.value, 0);
          if (!bucketPriorYear) return;
          const x = `${total.year}-12`;
          const row = byPeriod[x] = byPeriod[x] || { x };
          row.working = (bucketPriorYear * (total.fees / priorTotal)) / 12 / 1e6;
        });
      }
      return { glGroup: group.glGroup, rollup: group.rollup, data: Object.values(byPeriod).sort((a, b) => a.x.localeCompare(b.x)) };
    };
    return { nir: pick('Non-Interest Income').map(build), nie: pick('Non-Interest Expense').map(build) };
  }, [glMonthly, workingPaths]);

  const balanceWaterfalls = useMemo(() => {
    // End label follows the latest actual month in the data — hardcoding it
    // (formerly 'May-26') goes stale the day a new actuals month lands.
    const lastActualPeriod = ((companyMonthly?.groups?.[0]?.series?.actuals) || [])
      .reduce((max, row) => (row.period > max ? row.period : max), '');
    const endLabel = lastActualPeriod
      ? `${['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][Number(lastActualPeriod.slice(5, 7)) - 1]}-${lastActualPeriod.slice(2, 4)}`
      : 'Latest';
    const build = (glGroupNames) => {
      const contributions = [];
      let startTotal = 0;
      let endTotal = 0;
      const byKey = {};
      (glGroupAnnual?.groups || [])
        .filter((g) => glGroupNames.includes(g.glGroup))
        .forEach((group) => {
          const actuals = group.series.actuals || [];
          const first = actuals.find((r) => r.period === '2024');
          const last = actuals[actuals.length - 1];
          if (!first || !last) return;
          const bucket = byKey[group.key] = byKey[group.key] || { start: 0, end: 0 };
          bucket.start += first.value;
          bucket.end += last.value;
        });
      Object.entries(byKey).forEach(([key, v]) => {
        startTotal += v.start;
        endTotal += v.end;
        contributions.push({ label: key, value: (v.end - v.start) / 1e6 });
      });
      contributions.sort((a, b) => Math.abs(b.value) - Math.abs(a.value));
      const top = contributions.slice(0, 8);
      const otherSum = contributions.slice(8).reduce((s, c) => s + c.value, 0);
      return floatRows([
        { label: 'Dec-24', total: true, value: startTotal / 1e6 },
        ...top,
        ...(contributions.length > 8 ? [{ label: 'All other', value: otherSum }] : []),
        { label: endLabel, total: true, value: endTotal / 1e6 }
      ]);
    };
    return {
      loans: build(['Loans']),
      deposits: build(['Transaction Deposits', 'Savings Deposits', 'Time Deposits']),
      endLabel
    };
  }, [glGroupAnnual, companyMonthly]);

  // ---- where it changed: movers + AI summaries ----
  const movers = useMemo(() => {
    const scoped = (entityAnnual?.groups || [])
      .filter((g) => divisionFilter === 'all' || g.division === divisionFilter)
      .filter((g) => marketFilter === 'all' || g.market === marketFilter);
    const rows = scoped.map((group) => {
      const actual = (group.series.actuals || []).find((r) => r.period === '2026');
      const prior = (group.series.prior || []).find((r) => r.period === '2026');
      if (!actual || !prior || !prior.netIncome) return null;
      const annualized = annualizeYtd(actual.netIncome, actual.months);
      const submission = (submissionsData?.submissions || []).find(
        (s) => s.division === group.division && s.market === group.market
      );
      return {
        key: group.key,
        unit: `${group.division} · ${group.market}`,
        submissionId: submission?.id ?? null,
        planningEntity: submission?.planningEntity ?? null,
        gapMm: (annualized - prior.netIncome) / 1e6,
        gapPct: ((annualized - prior.netIncome) / Math.abs(prior.netIncome)) * 100,
        aiSummary: submission?.aiSummary || null,
        justification: (submission?.narratives || []).find((n) => n.text)?.text || null,
        status: submission?.status
      };
    }).filter(Boolean);
    return rows.sort((a, b) => Math.abs(b.gapMm) - Math.abs(a.gapMm)).slice(0, 5);
  }, [entityAnnual, divisionFilter, marketFilter, submissionsData]);

  // Company return metrics for the peer pro-forma table (run-rate heuristics:
  // assets ~ loans/0.66, earning assets ~ 92% of assets, TCE ~ 8% of assets).
  const peerCompanyMetrics = useMemo(() => {
    const actuals = companyAnnualRows.actuals || {};
    const latest = actuals['2026'] || actuals['2025'];
    if (!latest || !latest.loans) return null;
    const months = latest.months || 12;
    const annualize = (value) => (value / months) * 12;
    const assets = latest.loans / 0.66;
    const previous = actuals[String(Number(latest === actuals['2026'] ? 2026 : 2025) - 1)];
    const loanGrowth = previous?.loans
      ? ((latest.loans / previous.loans) - 1) * 100 * (12 / months)
      : 3;
    const niAnnual = annualize(latest.netIncome);
    const niiAnnual = annualize(latest.nii);
    const roa = (niAnnual / assets) * 100;
    return {
      roa,
      rotce: (niAnnual / (assets * 0.08)) * 100,
      nim: (niiAnnual / (assets * 0.92)) * 100,
      efficiency: (annualize(latest.expense) / (niiAnnual + annualize(latest.fees))) * 100,
      cet1: 11.7 - (
        loanGrowth * execMacroSettings.rwaLoanGrowthCoefficient + execMacroSettings.creditLoss * 0.35
      ) * 0.08 - execMacroSettings.creditLoss * 0.18,
      loanGrowth
    };
  }, [companyAnnualRows, execMacroSettings]);

  // Company-level guardrails: derived from consolidated balances/growth under
  // the active macro settings — PRD OP_06 set.
  const guardrailRows = useMemo(() => {
    const actuals = companyAnnualRows.actuals || {};
    const prior = companyAnnualRows.prior || {};
    const latest = actuals['2026'] || actuals['2025'];
    if (!latest) return [];
    const growthOf = (series, year) => {
      const current = series[year];
      const previous = series[String(Number(year) - 1)];
      if (!current || !previous || !previous.loans) return null;
      return ((current.loans / previous.loans) - 1) * 100 * (12 / (current.months || 12));
    };
    const loanGrowth = growthOf(actuals, '2026') ?? growthOf(actuals, '2025') ?? 4;
    const ldr = latest.deposits ? (latest.loans / latest.deposits) * 100 : 90;
    const runoff = execMacroSettings.depositRunoff;
    const creditLoss = execMacroSettings.creditLoss;
    const rwaGrowth = loanGrowth * execMacroSettings.rwaLoanGrowthCoefficient + creditLoss * 0.35;
    const lcr = 126 - runoff * 1.8 - Math.max(0, loanGrowth - 5) * 0.9;
    const cet1 = 11.7 - rwaGrowth * 0.08 - creditLoss * 0.18;
    const leverage = 9.1 - loanGrowth * 0.04;
    const rbc = 14.2 - rwaGrowth * 0.07 - creditLoss * 0.12;
    const wholesale = 7.2 + runoff * 0.72 + Math.max(0, ldr - 92) * 0.35;
    const cre = 242 + loanGrowth * 2.5;
    const appetiteCap = execMacroSettings.loanGrowthAppetiteCap;

    const priorYears = ['2027', '2028', '2029', '2030'];
    const priorGrowthDots = (min, max, green, yellow, direction) =>
      priorYears
        .map((year) => {
          const growth = growthOf(prior, year);
          if (growth === null) return null;
          return {
            year: `FY${year}`,
            value: growth,
            display: `${growth.toFixed(1)}%`,
            status: statusFor(growth, green, yellow, direction),
            position: toBulletPercent(growth, min, max)
          };
        })
        .filter(Boolean);

    const simple = (metric, value, direction, min, max, target, yellow, driver, digits = 1) => ({
      metric,
      value,
      display: `${value.toFixed(digits)}%`,
      status: statusFor(value, target, yellow, direction),
      min, max, target,
      targetLabel: `${direction === 'higher' ? '>=' : '<='} ${target}%`,
      bands: direction === 'higher'
        ? higherIsBetterBands(min, yellow, target, max)
        : lowerIsBetterBands(min, target, yellow, max),
      yearPoints: [],
      threshold: direction === 'higher'
        ? `Watch below ${target}%, breach below ${yellow}%`
        : `Watch above ${target}%, breach above ${yellow}%`,
      driver
    });

    return [
      simple('LCR', lcr, 'higher', 90, 135, 118, 108, 'Consolidated runoff and loan growth funding need'),
      simple('CET1', cet1, 'higher', 7.5, 13, 10.5, 8.5, 'RWA growth from consolidated production'),
      simple('Tier 1 Leverage', leverage, 'higher', 5, 11, 8.2, 6.2, 'Balance sheet expansion'),
      simple('Total RBC', rbc, 'higher', 9, 16, 12.5, 10.5, 'Credit stress and loan growth'),
      simple('Loan-to-Deposit', ldr, 'lower', 70, 105, 92, 98, 'Loans vs core deposit gathering'),
      simple('Wholesale Funding', wholesale, 'lower', 0, 20, 12, 16, 'Funding gap under liquidity stress'),
      { ...simple('CRE Concentration', cre, 'lower', 200, 320, 275, 300, 'Commercial production mix', 0) },
      {
        ...simple('Loan Growth vs Appetite', loanGrowth, 'lower', 0, 12, appetiteCap - 1.5, appetiteCap,
          'Centrally set growth appetite cap'),
        yearPoints: priorGrowthDots(0, 12, appetiteCap - 1.5, appetiteCap, 'lower')
      }
    ];
  }, [companyAnnualRows, execMacroSettings]);

  const scopeLabel = divisionFilter === 'all' && marketFilter === 'all'
    ? 'Total company' : `${divisionFilter === 'all' ? 'All divisions' : divisionFilter}${marketFilter === 'all' ? '' : ` · ${marketFilter}`}`;

  return (
    <div className="forecast-module forecast-exec">
      <section className="forecast-command">
        <div>
          <div className="forecast-kicker">
            <Activity size={14} />
            Forecast Console / Executive review
            <Link to="/forecasting" className="forecast-kicker-back"><ArrowLeft size={12} /> Cycle home</Link>
          </div>
          <h1>Executive review — {scopeLabel}</h1>
          <p>Plan bridges by year, balance and revenue trends, and the units that moved — scope with the filters.</p>
        </div>
        <div className="forecast-command-grid exec-controls">
          <label>
            Group by
            <select value={groupBy} onChange={(e) => setGroupBy(e.target.value)}>
              <option value="division">Division</option>
              <option value="market">Market</option>
            </select>
          </label>
          <label>
            Division
            <select value={divisionFilter} onChange={(e) => { setDivisionFilter(e.target.value); setMarketFilter('all'); }}>
              <option value="all">All divisions</option>
              {divisions.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
          </label>
          <label>
            Market
            <select value={marketFilter} onChange={(e) => setMarketFilter(e.target.value)}>
              <option value="all">All markets</option>
              {markets.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          </label>
          <label>
            GL group
            <select value={glFilter} onChange={(e) => setGlFilter(e.target.value)}>
              <option value="all">All GL groups</option>
              {glGroups.map((g) => <option key={g} value={g}>{g}</option>)}
            </select>
          </label>
          <button type="button" className="forecast-home-link" onClick={() => window.print()}>
            <Printer size={14} /> Print
          </button>
        </div>
      </section>

      {error && <div className="forecast-panel forecast-home-error"><p>{error}</p></div>}

      <section className="forecast-basis-tabs exec">
        <div className="forecast-segmented">
          {['GAAP', 'FTP'].map((candidate) => (
            <button
              type="button"
              key={candidate}
              className={basis === candidate ? 'active' : ''}
              onClick={() => setBasis(candidate)}
            >
              {candidate === 'GAAP' ? 'GAAP basis' : 'FTP basis (internal)'}
            </button>
          ))}
        </div>
        <span className="forecast-health-chip neutral">{execMacroChipLabel}</span>
        <Link className="forecast-health-chip neutral" to="/forecasting/macro?tab=compare">
          Macro isolation A/B
        </Link>
        <small>
          {basis === 'GAAP'
            ? 'As-reported results — primary basis for executive review'
            : `Internal profitability: NII split into lending spread, deposit franchise, and treasury mismatch at a single-rate transfer curve (${execMacroSettings.ftpRatePct.toFixed(2)}%)`}
        </small>
      </section>

      {basis === 'FTP' && (
        <section className="forecast-panel">
          <div className="forecast-panel-head">
            <span className="forecast-section-code">EX_00 ● NII composition — FTP basis</span>
            <h2>Lending spread vs deposit franchise vs treasury mismatch ($MM, by year)</h2>
          </div>
          <div className="forecast-home-table-wrap">
            <table className="forecast-home-table">
              <thead>
                <tr>
                  <th>Year · series</th>
                  <th>Lending spread margin</th>
                  <th>Deposit franchise margin</th>
                  <th>Treasury mismatch</th>
                  <th>NII (GAAP tie-out)</th>
                </tr>
              </thead>
              <tbody>
                {['actuals', 'prior'].flatMap((seriesName) =>
                  Object.entries(companyAnnualRows[seriesName] || {})
                    .sort(([a], [b]) => a.localeCompare(b))
                    .map(([year, bucket]) => {
                      const periods = bucket.months || 12;
                      const split = splitNiiFtp(
                        {
                          interestIncome: bucket.interestIncome,
                          interestExpense: bucket.interestExpense,
                          nii: bucket.nii,
                          averageLoans: bucket.loans,
                          averageDeposits: bucket.deposits
                        },
                        execMacroSettings.ftpRatePct,
                        12 / periods // balances are year-end EOP proxies; flows cover `periods` months
                      );
                      return (
                        <tr key={`${seriesName}-${year}`}>
                          <td>
                            <strong>{year}</strong>
                            <span className="forecast-home-market"> · {seriesName === 'actuals' ? 'Actuals' : 'Prior plan'}{periods < 12 ? ` · ${periods}mo` : ''}</span>
                          </td>
                          <td>{formatMillions(split.lendingMargin / 1e6)}</td>
                          <td>{formatMillions(split.depositMargin / 1e6)}</td>
                          <td>{formatMillions(split.mismatch / 1e6)}</td>
                          <td>{formatMillions(bucket.nii / 1e6)}</td>
                        </tr>
                      );
                    })
                )}
              </tbody>
            </table>
          </div>
          <p className="forecast-submit-note">
            Prototype convention: single-rate transfer curve at {execMacroSettings.ftpRatePct.toFixed(2)}% applied to average
            balances; the three components tie to GAAP NII by construction.
          </p>
        </section>
      )}

      <section className="forecast-exec-headline">
        {headline.map((item) => (
          <div key={item.label}>
            <span>{item.label}</span>
            <strong className={item.tone || ''}>{item.value}</strong>
          </div>
        ))}
      </section>

      <section className="forecast-panel">
        <div className="forecast-panel-head">
          <span className="forecast-section-code">EX_01 ● Plan bridge by year</span>
          <h2>
            {bridgeYear === '2026' ? 'Prior plan → run-rate' : `FY${Number(bridgeYear) - 1} → FY${bridgeYear} (prior-cycle path)`} ·
            {bridgeMode === 'driver' && glFilter === 'all' ? ' by driver' : ` by ${groupBy}${glFilter !== 'all' ? ` · ${glFilter}` : ''}`} ($MM)
          </h2>
        </div>
        <div className="forecast-submit-chips" style={{ marginBottom: 8 }}>
          {BRIDGE_YEARS.map((year) => (
            <button key={year} type="button"
              className={`forecast-health-chip clickable ${bridgeYear === year ? 'gold' : 'neutral'}`}
              onClick={() => setBridgeYear(year)}>
              FY{year}
            </button>
          ))}
          <span style={{ width: 14 }} />
          <button type="button" className={`forecast-health-chip clickable ${bridgeMode === 'driver' ? 'gold' : 'neutral'}`} onClick={() => setBridgeMode('driver')}>By driver</button>
          <button type="button" className={`forecast-health-chip clickable ${bridgeMode === 'group' ? 'gold' : 'neutral'}`} onClick={() => setBridgeMode('group')}>By {groupBy}</button>
        </div>
        <WaterfallBar data={bridge} />
      </section>

      <div className="forecast-exec-pair">
        <section className="forecast-panel">
          <div className="forecast-panel-head">
            <span className="forecast-section-code">EX_02A ● Loans{coverageBadge('loans')}</span>
            <h2>EOP balances — actuals, prior plan, and the working forecast ($B)</h2>
          </div>
          <div style={{ width: '100%', height: 210 }}>
            <ResponsiveContainer>
              <ComposedChart data={balanceLines}>
                <CartesianGrid strokeDasharray="2 4" strokeOpacity={0.25} />
                <XAxis dataKey="x" minTickGap={46} />
                <YAxis tickFormatter={(v) => `$${v.toFixed(1)}B`} width={62} domain={['auto', 'auto']} />
                <Tooltip formatter={(v, n) => [`$${Number(v).toFixed(2)}B`, n]} />
                <Legend />
                <Line dataKey="loans_prior" name="Prior plan" stroke={MUTED} strokeDasharray="5 3" dot={false} strokeWidth={1.6} />
                <Line dataKey="loans_actuals" name="Actuals" stroke={GOLD} dot={false} strokeWidth={2.2} />
                <Line dataKey="loans_working" name="Working plan" stroke={GREEN} strokeWidth={2} dot={{ r: 3 }} connectNulls />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </section>
        <section className="forecast-panel">
          <div className="forecast-panel-head">
            <span className="forecast-section-code">EX_02B ● Deposits{coverageBadge('deposits')}</span>
            <h2>EOP balances — actuals, prior plan, and the working forecast ($B)</h2>
          </div>
          <div style={{ width: '100%', height: 210 }}>
            <ResponsiveContainer>
              <ComposedChart data={balanceLines}>
                <CartesianGrid strokeDasharray="2 4" strokeOpacity={0.25} />
                <XAxis dataKey="x" minTickGap={46} />
                <YAxis tickFormatter={(v) => `$${v.toFixed(1)}B`} width={62} domain={['auto', 'auto']} />
                <Tooltip formatter={(v, n) => [`$${Number(v).toFixed(2)}B`, n]} />
                <Legend />
                <Line dataKey="deposits_prior" name="Prior plan" stroke={MUTED} strokeDasharray="5 3" dot={false} strokeWidth={1.6} />
                <Line dataKey="deposits_actuals" name="Actuals" stroke={GOLD} dot={false} strokeWidth={2.2} />
                <Line dataKey="deposits_working" name="Working plan" stroke={GREEN} strokeWidth={2} dot={{ r: 3 }} connectNulls />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </section>
      </div>

      {workingPaths && (
        <p className="forecast-submit-note" style={{ margin: '-6px 0 10px' }}>
          Working plan line: {workingPaths.coverage} of {workingPaths.units} in-scope packages have a
          saved forecast path; the remainder are filled with their prior plan so the total stays comparable.
        </p>
      )}

      {execGrowthColumns.length > 2 && (
        <section className="forecast-panel">
          <ForecastGrowthMatrix
            title="EX_02G ● Baseline growth path"
            columns={execGrowthColumns}
            note="Prior-cycle plan years versus actual history at the current filter scope — the embedded growth rate of the baseline before this cycle's packages land. Deltas compare each column to its left neighbor; expense and provision invert the color."
          />
        </section>
      )}

      <div className="forecast-exec-pair">
        <section className="forecast-panel">
          <div className="forecast-panel-head">
            <span className="forecast-section-code">EX_02C ● Loan balance walk</span>
            <h2>Dec-24 → {balanceWaterfalls.endLabel} by {groupBy} ($MM)</h2>
          </div>
          <WaterfallBar data={balanceWaterfalls.loans} height={250} />
        </section>
        <section className="forecast-panel">
          <div className="forecast-panel-head">
            <span className="forecast-section-code">EX_02D ● Deposit balance walk</span>
            <h2>Dec-24 → {balanceWaterfalls.endLabel} by {groupBy} ($MM)</h2>
          </div>
          <WaterfallBar data={balanceWaterfalls.deposits} height={250} />
        </section>
      </div>

      {['nir', 'nie'].map((side) => (
        <section className="forecast-panel" key={side}>
          <div className="forecast-panel-head">
            <span className="forecast-section-code">{side === 'nir' ? 'EX_02E ● Major NIR buckets' : 'EX_02F ● Major NIE buckets'}</span>
            <h2>Top {side === 'nir' ? 'revenue' : 'expense'} lines — actual vs prior plan ($MM monthly)</h2>
          </div>
          <div className="forecast-ribbon-grid">
            {(side === 'nir' ? bucketMultiples.nir : bucketMultiples.nie).map((bucket) => (
              <div className="forecast-ribbon" key={bucket.glGroup}>
                <h4>{bucket.glGroup}</h4>
                <div style={{ width: '100%', height: 120 }}>
                  <ResponsiveContainer>
                    <ComposedChart data={bucket.data}>
                      <XAxis dataKey="x" hide />
                      <YAxis hide domain={['auto', 'auto']} />
                      <Tooltip formatter={(v, n) => [`$${Number(v).toFixed(2)}MM`, n]} labelFormatter={(l) => l} />
                      <Line dataKey="prior" name="Prior plan" stroke={MUTED} strokeDasharray="4 3" dot={false} strokeWidth={1.4} />
                      <Line dataKey="actuals" name="Actuals" stroke={GOLD} dot={false} strokeWidth={2} />
                      <Line dataKey="working" name="Working plan (pro-rata)" stroke={GREEN} strokeWidth={1.8} dot={{ r: 2.5 }} connectNulls />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              </div>
            ))}
          </div>
        </section>
      ))}

      <section className="forecast-panel">
        <div className="forecast-panel-head">
          <span className="forecast-section-code">EX_03 ● Where it changed</span>
          <h2>Largest movers vs prior cycle — with the package's own explanation</h2>
        </div>
        <div className="exec-movers">
          {movers.map((mover) => (
            <div className="exec-mover" key={mover.key}>
              <div className="exec-mover-head">
                {mover.submissionId ? (
                  <Link to={`/forecasting/review/${mover.submissionId}`} className="exec-mover-title-link">
                    <strong>{mover.unit}</strong>
                  </Link>
                ) : (
                  <strong>{mover.unit}</strong>
                )}
                <span className={mover.gapMm >= 0 ? 'pos' : 'neg'}>
                  {formatMillions(mover.gapMm)} ({formatPct(mover.gapPct)})
                </span>
                {mover.status && <span className="forecast-status-chip neutral">{mover.status.replace('_', ' ')}</span>}
              </div>
              {mover.aiSummary ? (
                <p className="exec-mover-summary">{mover.aiSummary.headline}</p>
              ) : (
                <p className="exec-mover-summary muted">No package summary yet — run-rate signal only.</p>
              )}
              {mover.justification && (
                <p className="exec-mover-just">“{mover.justification}”</p>
              )}
              {mover.submissionId && (
                <div className="exec-mover-actions">
                  <Link to={`/forecasting/review/${mover.submissionId}`} className="forecast-home-action">
                    Review package
                  </Link>
                  {mover.planningEntity && (
                    <Link
                      to={`/forecasting/statements?level=entity&entity=${mover.planningEntity}`}
                      className="forecast-home-action ghost"
                    >
                      Statements
                    </Link>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      <section className="forecast-panel">
        <div className="forecast-panel-head">
          <span className="forecast-section-code">EX_03B ● AI executive narrative</span>
          <h2>Consolidated story, drafted top-down from server-computed contributions</h2>
        </div>
        <div className="forecast-submit-chips" style={{ marginBottom: 10 }}>
          <span className="forecast-health-chip gold">AI-drafted — review before distribution</span>
          <label className="forecast-health-chip neutral" style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
            Materiality
            <select value={materialityPct} onChange={(e) => setMaterialityPct(Number(e.target.value))}>
              {[5, 10, 15].map((pct) => <option key={pct} value={pct}>{pct}% of NI move</option>)}
            </select>
          </label>
          <button type="button" className="forecast-health-chip clickable gold" onClick={draftNarrative} disabled={narrativeBusy}>
            {narrativeBusy ? 'Drafting…' : narrative ? 'Regenerate' : 'Draft narrative'}
          </button>
        </div>
        {!narrative && !narrativeBusy && (
          <p className="forecast-submit-note">
            Drafts an executive summary anchored to the consolidated numbers: contribution analysis
            (server-computed) decides the main story; bottoms-up package narratives are quoted only when
            their unit is material at the top of the house, restated in consolidated terms; sub-threshold
            flags route to watch items.
          </p>
        )}
        {narrative?.status === 'error' && (
          <p className="forecast-submit-note" style={{ color: 'var(--destructive, #d9534f)' }}>
            Draft failed: {String(narrative.detail)} — regenerate to retry.
          </p>
        )}
        {narrative?.status === 'ok' && (
          <div className="exec-narrative">
            <p style={{ fontSize: 16, fontWeight: 600, margin: '4px 0 10px' }}>{narrative.headline}</p>
            {[['Top of house', narrative.topOfHouse], ['By division', narrative.divisionPivot],
              ['By market', narrative.marketPivot], ['By GL line', narrative.glPivot]].map(([label, rows]) => (
              (rows || []).length > 0 && (
                <div key={label} style={{ marginBottom: 8 }}>
                  <span className="forecast-section-code">{label}</span>
                  {rows.map((row) => <p key={row} style={{ margin: '3px 0' }}>{row}</p>)}
                </div>
              )
            ))}
            {(narrative.watchItems || []).length > 0 && (
              <div style={{ borderTop: '1px solid var(--ace-line, #2a3850)', paddingTop: 8, marginTop: 10 }}>
                <span className="forecast-section-code">Watch items — below the materiality line, visible by design</span>
                {narrative.watchItems.map((item) => (
                  <p key={item.unit + item.note.slice(0, 16)} style={{ margin: '3px 0' }}>
                    <strong>{item.unit}:</strong> {item.note}
                    {item.sourceRef && <span className="forecast-home-market"> · {item.sourceRef}</span>}
                  </p>
                ))}
              </div>
            )}
            <p className="forecast-submit-note" style={{ marginTop: 10 }}>
              {narrative.materialityNote} Basis: {narrative.basis}. Model: {narrative.model || '—'}.
              {(narrative.references || []).length > 0 && (
                <> Sources: {narrative.references.map((ref) => (
                  <span key={ref} className="forecast-health-chip neutral" style={{ marginLeft: 4 }}>{ref}</span>
                ))}</>
              )}
            </p>
          </div>
        )}
      </section>

      <section className="forecast-panel">
        <div className="forecast-panel-head">
          <span className="forecast-section-code">EX_04 ● Company guardrails</span>
          <h2>Capital, liquidity, return, and appetite — total company under active macro settings</h2>
          <span className="forecast-health-chip neutral">{execMacroChipLabel}</span>
        </div>
        <ForecastGuardrailBoard rows={guardrailRows} />
        <p className="forecast-submit-note">
          Derived from consolidated actual balances and growth with macro assumptions
          (deposit runoff {execMacroSettings.depositRunoff.toFixed(1)}%, credit loss {execMacroSettings.creditLoss.toFixed(2)}%);
          year dots show the prior-cycle plan path. Unit
          packages contribute the chips below.
        </p>
        <div className="forecast-submit-chips">
          {(() => {
            const snapshots = (submissionsData?.submissions || [])
              .map((s) => s.modelSnapshot?.constraintSummary).filter(Boolean);
            if (snapshots.length === 0) return <span className="forecast-health-chip neutral">No unit packages with guardrail snapshots yet.</span>;
            const totals = snapshots.reduce((sum, s) => ({
              green: sum.green + (s.green || 0), yellow: sum.yellow + (s.yellow || 0), red: sum.red + (s.red || 0)
            }), { green: 0, yellow: 0, red: 0 });
            return (
              <>
                <span className="forecast-health-chip green">{totals.green} green checks</span>
                <span className="forecast-health-chip yellow">{totals.yellow} watch</span>
                <span className={`forecast-health-chip ${totals.red ? 'red' : 'neutral'}`}>{totals.red} breach</span>
                <span className="forecast-health-chip neutral">{snapshots.length} units reporting</span>
              </>
            );
          })()}
        </div>
      </section>

      <section className="forecast-panel forecast-peer-section">
        <div className="forecast-panel-head">
          <span className="forecast-section-code">EX_05 ● Peer pro-forma comparison</span>
          <h2>Total company return metrics vs regional bank peers</h2>
        </div>
        {peerCompanyMetrics ? (
          <ForecastPeerProForma company={peerCompanyMetrics} contextLabel="Company run-rate" />
        ) : (
          <p className="forecast-submit-note">Company metrics load with the consolidated actuals.</p>
        )}
        <p className="forecast-submit-note">Peer data is illustrative until a licensed provider is wired (PRD §14).</p>
      </section>
    </div>
  );
};

export default ForecastExecView;
